import React from 'react';
import './EditServiceProvider.css';
import './mobile.css';
import { updateProvider } from '../../utils/firebase';
import { auth } from '../../config/firebase';

const EditServiceProvider = ({ 
  isOpen, 
  onClose, 
  provider, 
  categories,
  onSuccess 
}) => {
  const [editForm, setEditForm] = React.useState({
    name: provider?.name || '',
    category: provider?.category || '',
    phoneNumber: provider?.phoneNumber || '',
    email: provider?.email || '',
    address: provider?.address || '',
    location: provider?.location || '',
    description: provider?.description || '',
    isPriorityListing: provider?.isPriorityListing || false,
    businessHours: provider?.businessHours || {
      monday: { isOpen: false, open: '09:00', close: '17:00' },
      tuesday: { isOpen: false, open: '09:00', close: '17:00' },
      wednesday: { isOpen: false, open: '09:00', close: '17:00' },
      thursday: { isOpen: false, open: '09:00', close: '17:00' },
      friday: { isOpen: false, open: '09:00', close: '17:00' },
      saturday: { isOpen: false, open: '09:00', close: '17:00' },
      sunday: { isOpen: false, open: '09:00', close: '17:00' }
    }
  });

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (provider) {
      setEditForm({
        name: provider.name || '',
        category: provider.category || '',
        phoneNumber: provider.phoneNumber || '',
        email: provider.email || '',
        address: provider.address || '',
        location: provider.location || '',
        description: provider.description || '',
        isPriorityListing: provider.isPriorityListing || false,
        businessHours: provider.businessHours || editForm.businessHours
      });
    }
  }, [provider]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to update a provider');
      }

      console.log('Submitting update for provider:', provider.id);
      console.log('Update data:', editForm);

      await updateProvider(provider.id, {
        ...editForm,
        lastUpdated: new Date(),
        updatedBy: user.uid,
        userId: provider.userId || user.uid
      });

      console.log('Update successful');
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating provider:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="edit-provider-overlay">
      <div className="edit-provider-modal">
        <header className="edit-provider-header">
          <h2>Edit Service Provider</h2>
          <button onClick={onClose} className="close-button">
            <i className="fas fa-times"></i>
          </button>
        </header>

        <div className="edit-provider-content">
          <form onSubmit={handleSubmit} className="edit-provider-form">
            {/* Basic Information Section */}
            <div className="form-section">
              <h3 className="form-section-title">
                <i className="fas fa-info-circle"></i>
                Basic Information
              </h3>
              <div className="form-groups-container">
                <div className="form-group">
                  <label>Business Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    placeholder="Enter business name"
                  />
                </div>
                
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={editForm.category}
                    onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="form-section">
              <h3 className="form-section-title">
                <i className="fas fa-address-card"></i>
                Contact Information
              </h3>
              <div className="form-groups-container">
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={editForm.phoneNumber}
                    onChange={(e) => setEditForm({...editForm, phoneNumber: e.target.value})}
                    placeholder="Enter phone number"
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    placeholder="Enter email address"
                  />
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={editForm.address}
                    onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                    placeholder="Enter physical address"
                  />
                </div>

                <div className="form-group">
                  <label>Location/Area</label>
                  <input
                    type="text"
                    value={editForm.location}
                    onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                    placeholder="e.g., Sandown, Sandton"
                  />
                </div>
              </div>
            </div>

            {/* Description Section */}
            <div className="form-section">
              <h3 className="form-section-title">
                <i className="fas fa-align-left"></i>
                Business Description
              </h3>
              <div className="form-group">
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  placeholder="Enter business description"
                  rows="4"
                />
              </div>
            </div>

            {/* Add Priority Listing Section before Business Hours */}
            <div className="form-section">
              <h3 className="form-section-title">
                <i className="fas fa-star" style={{ color: '#4299e1' }}></i>
                Priority Listing Status
              </h3>
              <div className="priority-listing-control">
                <label className="priority-checkbox">
                  <input
                    type="checkbox"
                    checked={editForm.isPriorityListing}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      isPriorityListing: e.target.checked
                    })}
                  />
                  <span className="checkbox-label">Priority Search Listing</span>
                </label>
                <p className="priority-info">
                  <i className="fas fa-info-circle"></i>
                  Priority listings appear at the top of search results in their category
                </p>
              </div>
            </div>

            {/* Business Hours Section */}
            <div className="form-section">
              <h3 className="form-section-title">
                <i className="fas fa-clock"></i>
                Business Hours
              </h3>
              <div className="business-hours-section">
                {Object.entries(editForm.businessHours).map(([day, hours]) => (
                  <div key={day} className="business-hours-row">
                    <label className="day-label">
                      <input
                        type="checkbox"
                        checked={hours.isOpen}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          businessHours: {
                            ...editForm.businessHours,
                            [day]: { ...hours, isOpen: e.target.checked }
                          }
                        })}
                      />
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </label>
                    {hours.isOpen && (
                      <div className="hours-inputs">
                        <input
                          type="time"
                          value={hours.open}
                          onChange={(e) => setEditForm({
                            ...editForm,
                            businessHours: {
                              ...editForm.businessHours,
                              [day]: { ...hours, open: e.target.value }
                            }
                          })}
                        />
                        <span className="hours-separator">to</span>
                        <input
                          type="time"
                          value={hours.close}
                          onChange={(e) => setEditForm({
                            ...editForm,
                            businessHours: {
                              ...editForm.businessHours,
                              [day]: { ...hours, close: e.target.value }
                            }
                          })}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="save-btn" 
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditServiceProvider; 